import React from 'react';

interface Options {
  placeholder?: unknown;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

/**
 * Uses the placeholder as the accessible name only when the field has no other name:
 * no aria-label, no aria-labelledby and no associated <label> (wrapping or via htmlFor).
 * Otherwise a hint like "e.g. 500" would override the visible label.
 */
export function usePlaceholderLabel<T extends HTMLInputElement | HTMLTextAreaElement>({
  placeholder,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: Options) {
  const ref = React.useRef<T | null>(null);
  // Assume a label exists until the DOM says otherwise, so server and first client render agree
  const [hasLabelElement, setHasLabelElement] = React.useState(true);

  React.useEffect(() => {
    setHasLabelElement(!!ref.current?.labels?.length);
  });

  const fallback =
    !hasLabelElement && !ariaLabelledBy && typeof placeholder === 'string' && placeholder ? placeholder : undefined;

  return { ref, ariaLabel: ariaLabel ?? fallback };
}
